import { useState, useEffect } from "react";
import { useParams } from "react-router-dom";
import axios from "axios";
import { API } from "@/App";
import { CheckCircle, XCircle, MapPin, Package, Calendar, Clock, Loader2 } from "lucide-react";

export default function Confirmation() {
  const { id } = useParams();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [choice, setChoice] = useState("");
  const [error, setError] = useState("");

  useEffect(() => {
    (async () => {
      try {
        const res = await axios.get(`${API}/public/confirmation/${id}`);
        setData(res.data);
        setChoice(res.data.confirmation_presence || "");
      } catch (e) {
        setError(e?.response?.data?.detail || "Livraison introuvable");
      } finally {
        setLoading(false);
      }
    })();
  }, [id]);

  const submit = async (c) => {
    setSubmitting(true);
    try {
      await axios.post(`${API}/public/confirmation/${id}`, { choice: c });
      setChoice(c);
      setData({ ...data, confirmation_presence: c });
    } catch (e) {
      setError(e?.response?.data?.detail || "Erreur");
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-slate-50">
        <Loader2 className="w-8 h-8 animate-spin text-indigo-600" />
      </div>
    );
  }

  if (error && !data) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-slate-50 p-4">
        <div className="bg-white rounded-2xl p-6 max-w-md text-center shadow-xl">
          <XCircle className="w-14 h-14 text-red-500 mx-auto mb-3" />
          <h1 className="text-xl font-bold text-slate-800">Lien invalide</h1>
          <p className="text-sm text-slate-500 mt-2">{error}</p>
        </div>
      </div>
    );
  }

  const isConfirmed = choice === "confirme";
  const isAbsent = choice === "absent";

  return (
    <div className="min-h-screen bg-gradient-to-br from-indigo-50 via-white to-emerald-50 p-4 flex items-center justify-center">
      <div className="w-full max-w-md bg-white rounded-2xl shadow-2xl overflow-hidden" data-testid="confirmation-page">
        <div className="bg-gradient-to-r from-indigo-600 to-indigo-700 text-white p-5 text-center">
          <div className="text-3xl font-bold mb-1">TopCocoon</div>
          <p className="text-indigo-100 text-sm">Confirmation de livraison</p>
        </div>

        <div className="p-6 space-y-4">
          <div>
            <p className="text-sm text-slate-500">Bonjour,</p>
            <h1 className="text-2xl font-bold text-slate-800">{data.client_nom}</h1>
          </div>

          <div className="bg-slate-50 rounded-xl p-4 space-y-2 border">
            {data.date_livraison_fr && (
              <div className="flex items-start gap-2 text-sm">
                <Calendar className="w-4 h-4 text-indigo-600 mt-0.5 flex-shrink-0" />
                <span className="text-slate-700 capitalize">{data.date_livraison_fr}</span>
              </div>
            )}
            {data.heure_estimee && (
              <div className="flex items-start gap-2 text-sm">
                <Clock className="w-4 h-4 text-indigo-600 mt-0.5 flex-shrink-0" />
                <span className="text-slate-700">Créneau estimé : <strong>{data.heure_estimee}</strong></span>
              </div>
            )}
            <div className="flex items-start gap-2 text-sm">
              <MapPin className="w-4 h-4 text-indigo-600 mt-0.5 flex-shrink-0" />
              <span className="text-slate-700">{data.adresse}, {data.code_postal} {data.ville}</span>
            </div>
            {data.details_commande && (
              <div className="flex items-start gap-2 text-sm">
                <Package className="w-4 h-4 text-indigo-600 mt-0.5 flex-shrink-0" />
                <span className="text-slate-700">{data.details_commande}</span>
              </div>
            )}
            {data.prix != null && (
              <div className="pt-2 border-t text-sm">
                <span className="text-slate-500">Total à régler : </span>
                <span className="font-bold text-emerald-700">{data.prix} € (espèces uniquement)</span>
              </div>
            )}
          </div>

          {isConfirmed && (
            <div className="bg-emerald-50 border-2 border-emerald-200 rounded-xl p-4 text-center" data-testid="confirmed-message">
              <CheckCircle className="w-10 h-10 text-emerald-600 mx-auto mb-2" />
              <p className="font-bold text-emerald-800">Merci, votre présence est confirmée !</p>
              <p className="text-xs text-emerald-700 mt-1">Vous pouvez fermer cette page.</p>
            </div>
          )}

          {isAbsent && (
            <div className="bg-red-50 border-2 border-red-200 rounded-xl p-4 text-center" data-testid="absent-message">
              <XCircle className="w-10 h-10 text-red-600 mx-auto mb-2" />
              <p className="font-bold text-red-800">Indisponibilité enregistrée.</p>
              <p className="text-xs text-red-700 mt-1">Nous vous recontacterons pour reprogrammer.</p>
            </div>
          )}

          {!isConfirmed && !isAbsent && (
            <div className="space-y-3">
              <p className="text-center text-sm text-slate-600 font-medium">Serez-vous présent à la livraison ?</p>
              <button
                disabled={submitting}
                onClick={() => submit("confirme")}
                className="w-full h-14 rounded-xl bg-emerald-600 hover:bg-emerald-700 text-white font-bold text-lg flex items-center justify-center gap-2 disabled:opacity-50 transition"
                data-testid="confirm-btn"
              >
                <CheckCircle className="w-6 h-6" /> Oui, je confirme
              </button>
              <button
                disabled={submitting}
                onClick={() => submit("absent")}
                className="w-full h-12 rounded-xl bg-white border-2 border-red-300 text-red-700 hover:bg-red-50 font-semibold flex items-center justify-center gap-2 disabled:opacity-50 transition"
                data-testid="absent-btn"
              >
                <XCircle className="w-5 h-5" /> Non, je ne serai pas disponible
              </button>
            </div>
          )}

          {(isConfirmed || isAbsent) && (
            <button
              onClick={() => { setChoice(""); setData({ ...data, confirmation_presence: "en_attente" }); }}
              className="w-full text-xs text-slate-400 hover:text-slate-600 underline"
              data-testid="change-response-btn"
            >
              Changer ma réponse
            </button>
          )}
        </div>

        <div className="text-center text-xs text-slate-400 py-3 border-t bg-slate-50">
          Une question ? Contactez-nous — L'équipe TopCocoon
        </div>
      </div>
    </div>
  );
}
